import { create } from 'zustand';
import { API } from '../config/api';

const useSupportStore = create((set, get) => ({
  tickets: [],
  loading: false,
  error: null,

  // Fetch all tickets (for admin panel)
  fetchTickets: async (status = 'all') => {
    set({ loading: true, error: null });
    try {
      const url = status === 'all'
        ? `${API.SUPPORT}/tickets`
        : `${API.SUPPORT}/tickets?status=${status}`;
      const response = await fetch(url);
      if (!response.ok) throw new Error('Failed to fetch tickets');
      const data = await response.json();
      set({ tickets: data.tickets, loading: false });
    } catch (error) {
      console.error('Error fetching tickets:', error);
      set({ error: error.message, loading: false });
    }
  },

  // Get ticket by user (for chat widget)
  getUserTicket: async (userId, userEmail) => {
    try {
      const response = await fetch(
        `${API.SUPPORT}/tickets/user/${userId}?email=${encodeURIComponent(userEmail)}`
      );
      if (!response.ok) throw new Error('Failed to get user ticket');
      const data = await response.json();

      // Update local state to trigger sync in components
      if (data.ticket) {
        set((state) => {
          const existingIndex = state.tickets.findIndex(t => t.id === data.ticket.id);
          if (existingIndex >= 0) {
            // Update existing ticket
            const newTickets = [...state.tickets];
            newTickets[existingIndex] = data.ticket;
            return { tickets: newTickets };
          } else {
            // Add new ticket
            return { tickets: [data.ticket, ...state.tickets] };
          }
        });
      }

      return data.ticket;
    } catch (error) {
      console.error('Error getting user ticket:', error);
      return null;
    }
  },

  // Create new ticket from chat widget
  createTicket: async (userData, firstMessage) => {
    try {
      const response = await fetch(`${API.SUPPORT}/tickets`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          userId: userData.id,
          userName: userData.name || 'Користувач',
          userRole: userData.role === 'teacher' ? 'Вчитель' : 'Учень',
          email: userData.email || 'anonymous@user.com',
          firstMessage
        })
      });

      if (!response.ok) throw new Error('Failed to create ticket');
      const data = await response.json();

      // Add to local state
      set((state) => ({
        tickets: [data.ticket, ...state.tickets]
      }));

      return data.ticket;
    } catch (error) {
      console.error('Error creating ticket:', error);
      return null;
    }
  },

  // Add message to existing ticket (from user via chat widget)
  addUserMessage: async (ticketId, text) => {
    try {
      const response = await fetch(`${API.SUPPORT}/tickets/${ticketId}/messages`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ text, sender: 'user' })
      });

      if (!response.ok) throw new Error('Failed to add message');
      const data = await response.json();

      // Update local state
      set((state) => ({
        tickets: state.tickets.map(t =>
          t.id === ticketId
            ? { ...t, messages: [...t.messages, data.message], updatedAt: data.message.time }
            : t
        )
      }));

      return data.message;
    } catch (error) {
      console.error('Error adding user message:', error);
      return null;
    }
  },

  // Add message from admin (admin panel)
  addAdminMessage: async (ticketId, text) => {
    try {
      const response = await fetch(`${API.SUPPORT}/tickets/${ticketId}/admin-messages`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ text })
      });

      if (!response.ok) throw new Error('Failed to add message');
      const data = await response.json();

      // Update local state
      set((state) => ({
        tickets: state.tickets.map(t =>
          t.id === ticketId
            ? { ...t, messages: [...t.messages, data.message], updatedAt: data.message.time }
            : t
        )
      }));

      return data.message;
    } catch (error) {
      console.error('Error adding admin message:', error);
      return null;
    }
  },

  // Update ticket status
  setTicketStatus: async (ticketId, status) => {
    try {
      const response = await fetch(`${API.SUPPORT}/tickets/${ticketId}/status`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ status })
      });

      if (!response.ok) throw new Error('Failed to update status');
      const data = await response.json();

      // Update local state
      set((state) => ({
        tickets: state.tickets.map(t =>
          t.id === ticketId
            ? { ...t, status, updatedAt: data.ticket.updatedAt }
            : t
        )
      }));

      return true;
    } catch (error) {
      console.error('Error updating ticket status:', error);
      return false;
    }
  },

  // Get open tickets count (for badge)
  getOpenCount: () => {
    return get().tickets.filter(t => t.status === 'open').length;
  },

  // Set all tickets (for compatibility with existing code)
  setTickets: (tickets) => {
    set({ tickets });
  }
}));

export default useSupportStore;
